import * as React from 'react';
import PropTypes from 'prop-types';
import {
  OverlayTrigger,
  Tooltip,
  Row, Col,
  Form,
} from 'react-bootstrap-5';
import { isEmpty } from 'lodash';
import { isValidDate, parseDate, parseWithFormats, toISOStringWithOffset } from 'helpers/date_utils';

import PropertyTypeFilters from 'components/research_property/property_filters/property_type_filters';
import FilterSelect from 'components/search_sidebar/filter_select';
import TextInputPair from 'components/form/text_input_pair';
import DateInputPair from 'components/form/date_input_pair';
import ManagedInput from 'components/form/managed_input';
import LoadingSpinner from 'components/ui/loading_spinner';

import LocationsSection from 'components/requirements/locations_section/locations_section';
import { requirementShape, FIELDS } from 'components/requirements/types';

export default class RequirementsForm extends React.Component {
  lotUnits = [{ id: 'acres', value: 'acres', label: 'Acres' }];
  static propTypes = {
    errors: PropTypes.arrayOf(PropTypes.string).isRequired,
    fieldsToHide: PropTypes.arrayOf(PropTypes.string).isRequired,
    googleMapsApiKey: PropTypes.string.isRequired,
    investmentTypeOptions: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.number, label: PropTypes.string })
    ).isRequired,
    propertyTypeOptions: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.number, label: PropTypes.string })
    ).isRequired,
    propertyUseOptions: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.number, label: PropTypes.string })
    ).isRequired,
    requirement: requirementShape,
    saving: PropTypes.bool.isRequired,
    stateOptions: PropTypes.array.isRequired,
    transactionOptions: PropTypes.array.isRequired,
    onCancel: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onPropertyTypeChanged: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired
  };

  canSubmit = () => {
    return isEmpty(this.props.errors);
  }

  handleBuildingClassChange = (e) => {
    this.props.onChange(e.target.checked, e.target.name);
  }

  handlePropertyTypeChanged = (e) => {
    const checked = e.target.checked;
    const value = parseInt(e.target.value);

    this.props.onPropertyTypeChanged(value, checked);
  }

  handleInvestmentTypeChanged = (e) => {
    const { checked, value } = e.target;
    const type = parseInt(value);
    const { investmentTypes } = this.props.requirement;

    if (checked) {
      this.props.onChange([...investmentTypes, type], 'investmentTypes');
    } else {
      this.props.onChange(investmentTypes.filter(investmentType => investmentType !== type), 'investmentTypes');
    }
  }

  renderBuildingSizeInput() {
    const { requirement } = this.props;

    return (
      <TextInputPair
        inputRange={[0]}
        label="Building Size (SF)"
        maxKey="maxBuildingSize"
        maxValue={requirement.maxBuildingSize}
        minKey="minBuildingSize"
        minValue={requirement.minBuildingSize}
        name="Building Size"
        type="number"
        onChange={this.props.onChange}
      />
    );
  }

  renderPropertyUseSelect() {
    const { requirement } = this.props;

    return (
      <Form.Group className="form-group">
        <Form.Label>Property Use</Form.Label>
        <ManagedInput
          className="form-select"
          name="propertyUse"
          placeholder="Property Use"
          type="select"
          value={requirement.propertyUse}
          onValueChange={value => this.props.onChange(value, 'propertyUse')}
        >
          <option className="text-capitalize" value={''}></option>
          {this.props.propertyUseOptions.map(({ id, label }) => (
            <option className="text-capitalize" key={id} value={id}>{label}</option>
          ))}
        </ManagedInput>
      </Form.Group>
    );
  }

  renderTenancySelect() {
    const { requirement } = this.props;

    return (
      <Form.Group className="form-group">
        <Form.Label>Tenancy</Form.Label>
        <ManagedInput
          className="form-select"
          name="tenancyId"
          placeholder="Tenancy"
          type="select"
          value={requirement.tenancyId}
          onValueChange={value => this.props.onChange(value, 'tenancyId')}
        >
          {['', 'single', 'multiple'].map((item, idx) => (
            <option className="text-capitalize" key={idx} value={idx}>{item}</option>
          ))}
        </ManagedInput>
      </Form.Group>
    );
  }

  renderVacancyPctInput() {
    const { requirement } = this.props;

    return (
      <Form.Group className="form-group">
        <Form.Label>Max Vacancy Percentage</Form.Label>
        <ManagedInput
          className="form-control"
          minValue={0}
          name="maxVacancyPct"
          placeholder="Max Vacancy Percentage"
          step="any"
          type="number"
          value={requirement.maxVacancyPct}
          onValueChange={value => this.props.onChange(value, 'maxVacancyPct')}
        />
      </Form.Group>
    );
  }

  renderDockHighDoorsInput() {
    if (this.props.fieldsToHide.includes(FIELDS.DOCK_HIGH_DOORS)) return;

    const { requirement } = this.props;

    return (
      <TextInputPair
        inputRange={[0]}
        label="Dock High Doors"
        maxKey="maxDockHighDoors"
        maxValue={requirement.maxDockHighDoors}
        minKey="minDockHighDoors"
        minValue={requirement.minDockHighDoors}
        name="DockHighDoors"
        type="number"
        onChange={this.props.onChange}
      />
    );
  }

  renderCapRateInput() {
    const { requirement } = this.props;

    return (
      <TextInputPair
        inputRange={[1, 100]}
        label="Cap Rate"
        maxKey="maxCapRate"
        maxValue={requirement.maxCapRate}
        minKey="minCapRate"
        minValue={requirement.minCapRate}
        name="CapRate"
        type="number"
        onChange={this.props.onChange}
      />
    );
  }

  renderBuildingClassInputs() {
    const { requirement } = this.props;
    const boxes = [
      { key: 'buildingClassA', label: 'A' },
      { key: 'buildingClassB', label: 'B' },
      { key: 'buildingClassC', label: 'C' }
    ];

    return (
      <Form.Group className="form-group">
        <Form.Label>Building Class</Form.Label>
        <div>
          <Form.Group className="d-inline-flex">
            {boxes.map(box => (
              <span className="me-3" key={box.key}>
                <Form.Check
                  checked={requirement[box.key]}
                  className="my-0"
                  key={box.key}
                  label={box.label}
                  name={box.key}
                  type="checkbox"
                  onChange={this.handleBuildingClassChange}
                />
              </span>
            ))}
          </Form.Group>
        </div>
      </Form.Group>
    );
  }

  renderLotSizeInput() {
    if (this.props.fieldsToHide.includes(FIELDS.LOT_SIZE)) return;
    const { requirement } = this.props;

    return (
      <Row>
        <Col md={8}>
          <TextInputPair
            inputRange={[0]}
            label="Lot Size"
            maxKey="maxLotSize"
            maxValue={requirement.maxLotSize}
            minKey="minLotSize"
            minValue={requirement.minLotSize}
            name="Lot Size"
            type="number"
            onChange={this.props.onChange}
          />
        </Col>
        <Col className="pt-2" md={4}>
          <FilterSelect
            name="lotSizeUnits"
            options={this.lotUnits}
            placeholder="SF"
            value={requirement.lotSizeUnits}
            onChange={e => this.props.onChange(e.target.value, e.target.name)}
          />
        </Col>
      </Row>
    );
  }

  renderExpirationDateInput() {
    const { requirement } = this.props;

    return (
      <Form.Group className="form-group">
        <Form.Label>Expiration Date</Form.Label>
        <input
          className="form-control"
          name="expiration"
          type="date"
          value={requirement.expirationDate || ''}
          onChange={e => this.props.onChange(e.target.value, 'expirationDate')}
        />
      </Form.Group>
    );
  }

  renderSalePriceInput() {
    const { requirement } = this.props;

    return (
      <TextInputPair
        inputRange={[0]}
        label="Price"
        maxKey="maxSalePrice"
        maxValue={requirement.maxSalePrice}
        minKey="minSalePrice"
        minValue={requirement.minSalePrice}
        name="SalePrice"
        type="number"
        onChange={this.props.onChange}
      />
    );
  }

  renderUnitsInput() {
    const { requirement } = this.props;

    return (
      <div>
        <TextInputPair
          inputRange={[0]}
          label="Units"
          maxKey="maxNumberOfUnits"
          maxValue={requirement.maxNumberOfUnits}
          minKey="minNumberOfUnits"
          minValue={requirement.minNumberOfUnits}
          name="NumberOfUnits"
          type="number"
          onChange={this.props.onChange}
        />
      </div>
    );
  }

  renderNotesInput() {
    const { requirement } = this.props;
    return (
      <div>
        <Form.Group className="form-group">
          <ManagedInput
            className="form-control"
            name="notes"
            type="textarea"
            value={requirement.notes}
            onValueChange={value => this.props.onChange(value, 'notes')}
          />
        </Form.Group>
      </div>
    );
  }

  yearFromDateValue = (value) => {
    const yearOnly = parseWithFormats(value, 'YYYY', true);
    const date = isValidDate(yearOnly) ? yearOnly : parseDate(value);
    return isValidDate(date) ? date.getFullYear() : NaN;
  };

  renderYearBuiltInput() {
    const { requirement } = this.props;

    const maxValueFormatted = requirement.maxYearBuilt
      ? toISOStringWithOffset(new Date(requirement.maxYearBuilt, 0, 1)) : null;
    const minValueFormatted = requirement.minYearBuilt
      ? toISOStringWithOffset(new Date(requirement.minYearBuilt, 0, 1)) : null;

    return (
      <TextInputPair
        inputRange={[1800, 2100]}
        label="Year Built"
        managedInputProps={{
          showYearPicker: true
        }}
        maxKey="maxYearBuilt"
        maxValue={maxValueFormatted}
        minKey="minYearBuilt"
        minValue={minValueFormatted}
        name="YearBuilt"
        type="date"
        onChange={(value, key) => this.props.onChange(this.yearFromDateValue(value), key)}
      />
    );
  }

  propertyTypeIsChecked(propertyTypeId) {
    const { requirement: { assetClassIds } } = this.props;

    return (assetClassIds || []).includes(propertyTypeId);
  }

  investmentTypeIsChecked(investmentType) {
    const { requirement: { investmentTypes } } = this.props;

    return (investmentTypes || []).includes(investmentType);
  }

  renderPropertyTypeInput() {
    const { propertyTypeOptions } = this.props;
    const { assetClassIds } = this.props.requirement;

    return (
      <div className="pt-1 mb-4">
        <Form.Label>Property Type</Form.Label>
        <PropertyTypeFilters
          assetClasses={propertyTypeOptions}
          embedded
          selectedAssetClasses={(assetClassIds || []).map(type => type.toString())}
          onAssetClassChange={this.handlePropertyTypeChanged}
        />
      </div>
    );
  }

  renderInvestmentTypeInput() {
    const { investmentTypeOptions } = this.props;

    return (
      <Form.Group className="form-group">
        <Form.Label>Investment Type</Form.Label>
        {investmentTypeOptions.map(({ label, id }, i) => (
          <Form.Check
            checked={this.investmentTypeIsChecked(id)}
            className={i == 0 ? 'mt-0' : ''}
            key={id}
            label={label}
            name={`investment-type-${id}`}
            type="checkbox"
            value={id}
            onChange={this.handleInvestmentTypeChanged}
          />
        ))}
      </Form.Group>
    );
  }

  renderTransactionTypeSelect() {
    const { transactionOptions } = this.props;
    const { transactionType } = this.props.requirement;

    return (
      <FilterSelect
        label="Transaction Type *"
        name="transactionType"
        options={transactionOptions}
        value={transactionType}
        onChange={e => this.props.onChange(e.target.value, e.target.name)}
      />
    );
  }

  renderLocationSection() {
    const { requirement, stateOptions, googleMapsApiKey, onChange } = this.props;

    return (
      <LocationsSection
        googleMapsApiKey={googleMapsApiKey}
        locations={requirement.locations}
        stateOptions={stateOptions}
        onLocationsChange={newLocations => onChange(newLocations, 'locations')}
      />
    );
  }

  renderLeaseDateInput() {
    const { requirement } = this.props;

    return (
      <DateInputPair
        label="Lease Date"
        maxKey="maxLeaseDate"
        maxValue={requirement.maxLeaseDate}
        minKey="minLeaseDate"
        minValue={requirement.minLeaseDate}
        name="leaseDate"
        onChange={this.props.onChange}
      />
    );
  }

  renderNameInput() {
    const { name } = this.props.requirement;

    return (
      <div>
        <Form.Label>Requirement Name *</Form.Label>
        <Form.Group className="form-group">
          <ManagedInput
            className="form-control"
            name="name"
            value={name}
            onValueChange={value => this.props.onChange(value, 'name')}
          />
        </Form.Group>
      </div>
    );
  }

  renderWithTooltip = (button, placement) => {
    const tooltip = <Tooltip id="disable-save-button">{this.props.errors.join(', ')}</Tooltip>;

    return (
      <OverlayTrigger overlay={tooltip} placement={placement}>
        <span className="d-inline-block">{button}</span>
      </OverlayTrigger>
    );
  }

  renderSubmitButton = () => {
    const { onSave, saving, requirement } = this.props;
    const disabled = !this.canSubmit();
    const buttonLabel = requirement.id ? 'Update' : 'Save';

    return (
      <button
        className="btn btn-primary"
        disabled={disabled}
        name="save"
        style={disabled ? { pointerEvents: 'none' } : {}}
        onClick={onSave}
      >
        {saving ? (
          <>
            <LoadingSpinner flex size="md" skipTopBuffer />
            Saving ...
          </>
        ) : buttonLabel}
      </button>
    );
  }

  renderButtons() {
    return (
      <>
        <button className="btn btn-ghost me-3" onClick={this.props.onCancel}>Cancel</button>
        {this.canSubmit() ? this.renderSubmitButton() : this.renderWithTooltip(this.renderSubmitButton(), 'right')}
      </>
    );
  }

  renderSectionHeader(label) {
    return <h6 className="fw-bold w-100 mt-0 mb-2">{label}</h6>;
  }

  renderPropertyFields() {
    return (
      <>
        {this.renderSectionHeader('Property')}
        <div className="form-vertical-field-container">
          {this.renderPropertyTypeInput()}
          {this.renderLotSizeInput()}
          {this.renderBuildingSizeInput()}
          {this.renderUnitsInput()}
          {this.renderYearBuiltInput()}
          {this.renderDockHighDoorsInput()}
          {this.renderPropertyUseSelect()}
          {this.props.requirement.transactionType === 'sale' && (
            <>
              {this.renderTenancySelect()}
              {this.renderVacancyPctInput()}
            </>
          )}
          {this.renderBuildingClassInputs()}
        </div>
      </>
    );
  }

  renderTransactionFields() {
    const { transactionType } = this.props.requirement;

    return (
      <>
        {this.renderSectionHeader('Transaction')}
        {transactionType === 'sale' && (
          <>
            {this.renderSalePriceInput()}
            {this.renderCapRateInput()}
            {this.renderInvestmentTypeInput()}
          </>
        )}
        {transactionType === 'lease' && this.renderLeaseDateInput()}
      </>
    );
  }

  render() {
    return (
      <div>
        {this.renderSectionHeader('Details')}
        <div className="form-vertical-field-container">
          {this.renderNameInput()}
          {this.renderExpirationDateInput()}
          {this.renderTransactionTypeSelect()}
        </div>
        <hr className="my-5" />
        <Row>
          <Col md={6}>{this.renderPropertyFields()}</Col>
          <Col md={6}>{this.renderTransactionFields()}</Col>
        </Row>
        <hr className="mb-5" />
        {this.renderSectionHeader('Locations')}
        {this.renderLocationSection()}
        <hr className="mb-5" />
        {this.renderSectionHeader('Notes')}
        {this.renderNotesInput()}
        <div className="text-start mt-4">
          {this.renderButtons()}
        </div>
      </div>
    );
  }
}
